import type { CSSProperties, ReactNode } from 'react';

export interface IllustrationProps {
  /** Rendered width in px (the SVG keeps its aspect ratio). */
  readonly width?: number | undefined;
  /** Rendered height in px. */
  readonly height?: number | undefined;
  /** Meaningful illustration: becomes role="img" with this accessible name. Omit for decoration (aria-hidden). */
  readonly title?: string | undefined;
  readonly className?: string | undefined;
}

/** Design-token colours; each resolves against the host's light/dark theme. */
export const COLOR = {
  road: 'var(--color-road)',
  roadLine: 'var(--color-road-line)',
  grass: 'var(--color-grass)',
  car: 'var(--color-car)',
  cone: 'var(--color-cone)',
  danger: 'var(--color-danger)',
  onDanger: 'var(--color-on-danger)',
  dangerBg: 'var(--color-danger-bg)',
  primary: 'var(--color-primary)',
  onPrimary: 'var(--color-on-primary)',
  surface: 'var(--color-surface)',
  border: 'var(--color-border)',
} as const;

export function paint(fill: string, stroke?: string, strokeWidth?: number): CSSProperties {
  return {
    fill,
    ...(stroke === undefined ? {} : { stroke }),
    ...(strokeWidth === undefined ? {} : { strokeWidth }),
  };
}

export function IllustrationFrame({
  width,
  height,
  title,
  className,
  viewBox,
  children,
}: IllustrationProps & {
  readonly width: number;
  readonly height: number;
  readonly viewBox: string;
  readonly children: ReactNode;
}) {
  const labelled = title !== undefined && title !== '';
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={width}
      height={height}
      className={className}
      focusable="false"
      role={labelled ? 'img' : undefined}
      aria-hidden={labelled ? undefined : true}
      aria-label={labelled ? title : undefined}
    >
      {labelled ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}
